import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { administrationClient } from '@/shared/api-client';
import { apiClient, unwrap } from '@/shared/api-client/workspace';

/**
 * Bookings (PRD #186): reservations of play time at a Venue. The read path (#187) lists Bookings
 * placed on a day by their item SessionSlots (ADR 0048, #263); the create path (#189) reserves a
 * booking against one or more PassVariants, each frozen to a price snapshot server-side, and — for a
 * SESSION-pass line — an exact SessionSlot plus a frozen session-duration snapshot. The backend is
 * reached only through the typed `apiClient` + `unwrap` seam (ADR 0012); `booking.view` gates the
 * list and `booking.create` the create (403).
 */
export type BookingStatus = 'booked' | 'seated' | 'cancelled' | 'paid';

/** How a booking payment was taken (#193): cash or a card keyed in on an external terminal. */
export type PaymentMethod = 'cash' | 'card_manual';

export interface Booking {
  id: string;
  venueId: string;
  reference: string;
  guestName: string;
  partySize: number;
  status: BookingStatus;
}

export interface BookingList {
  items: Booking[];
}

export interface BookingItem {
  id: string;
  passVariantId: string;
  name: string;
  unitPriceAmount: number;
  unitPriceCurrency: string;
  quantity: number;
  /** The item's SessionSlot date, only for a SESSION-pass item (ADR 0048, #263). */
  sessionSlotDate?: string | null;
  /** The item's SessionSlot half-hour start time (`HH:mm:ss`), only for a SESSION-pass item. */
  sessionSlotStartTime?: string | null;
  sessionSlotOccurrence?: number | null;
  /** The frozen Pass session-duration snapshot in minutes, only for a SESSION-pass item. */
  sessionDurationMinutes?: number | null;
}

/** A {@link BookingItem}'s quantity unit's derived redemption state (ADR 0048, #264/#265). */
export type BookingUnitStatus = 'available' | 'redeemed' | 'expired' | 'cancelled' | 'refunded';
export type WaiverStatus = 'VALID' | 'EXPIRED' | 'REVOKED';

/**
 * One independently settleable unit of a {@link BookingItem}'s quantity (ADR 0048, #264/#265):
 * `cancellable`/`refundable` are backend-computed against the Booking's current status and the
 * Venue's configured cancellation cutoff, so the surface only ever renders backend policy rather
 * than re-deriving the window math itself.
 */
export interface BookingUnit {
  id: string;
  bookingItemId: string;
  unitIndex: number;
  status: BookingUnitStatus;
  requiresWaiver: boolean;
  waiverRecordId: string | null;
  waiverTemplateId: string | null;
  waiverTemplateVersion: number | null;
  waiverSignerName: string | null;
  waiverSignerCapacity: string | null;
  waiverChildName: string | null;
  waiverChildDateOfBirth: string | null;
  waiverCapturedAt: string | null;
  waiverExpiresAt: string | null;
  waiverLinkedAt: string | null;
  redeemedAt: string | null;
  cancelledAt: string | null;
  refundedAt: string | null;
  cancellable: boolean;
  refundable: boolean;
}

export interface WaiverRecord {
  id: string;
  templateId: string;
  templateVersion: number;
  status: WaiverStatus;
  signerName: string;
  signerCapacity: string;
  childName: string | null;
  capturedAt: string;
  expiresAt: string;
  revokedAt: string | null;
  revokedReason: string | null;
  createdAt: string;
}

export interface BookingDetail {
  id: string;
  venueId: string;
  reference: string;
  firstName: string;
  lastName: string;
  phone: string | null;
  email: string | null;
  partySize: number;
  status: BookingStatus;
  items: BookingItem[];
  units: BookingUnit[];
}

/**
 * Which slice of a Venue's Bookings the list shows. `today` and a specific `date` each scope to a
 * single day in the Venue's timezone; `upcoming` is everything from the start of today onward. The
 * backend resolves the day boundaries, so the client only names the view. A Booking whose items are
 * all slotless is placed on no day view (ADR 0048, #263).
 */
export type BookingDateFilter =
  | { kind: 'today' }
  | { kind: 'upcoming' }
  | { kind: 'date'; date: string };

export interface CreateBookingItemInput {
  passVariantId: string;
  quantity: number;
  /** Required for a SESSION-pass variant, omitted otherwise (ADR 0048, #263). */
  sessionSlotDate?: string;
  /** `HH:mm:ss` on the :00/:30 grid; required for a SESSION-pass variant, omitted otherwise. */
  sessionSlotStartTime?: string;
  sessionSlotOccurrence?: number;
}

export interface CreateBookingInput {
  firstName: string;
  lastName: string;
  phone?: string;
  email?: string;
  partySize: number;
  items: CreateBookingItemInput[];
}

type RevokeWaiverRecordInput = {
  waiverRecordId: string;
  reason: string;
};

const listKey = (venueId: string, filter: BookingDateFilter) =>
  ['venues', venueId, 'bookings', filter.kind, filter.kind === 'date' ? filter.date : ''] as const;

const detailKey = (venueId: string, bookingId: string) =>
  ['venues', venueId, 'bookings', bookingId] as const;

const bookingsKey = (venueId: string) => ['venues', venueId, 'bookings'] as const;
const waiverRecordKey = (waiverRecordId: string) => ['waiver-records', waiverRecordId] as const;

function filterQuery(filter: BookingDateFilter): { filter?: string; date?: string } {
  switch (filter.kind) {
    case 'upcoming':
      return { filter: 'upcoming' };
    case 'date':
      return { date: filter.date };
    default:
      return {};
  }
}

const getBookings = (
  venueId: string,
  filter: BookingDateFilter = { kind: 'today' },
): Promise<BookingList> =>
  unwrap(
    apiClient.GET('/api/v1/venue-workspace/venues/{venueId}/bookings', {
      params: { path: { venueId }, query: filterQuery(filter) },
    }),
  ) as Promise<BookingList>;

const getBooking = (venueId: string, bookingId: string): Promise<BookingDetail> =>
  unwrap(
    apiClient.GET('/api/v1/venue-workspace/venues/{venueId}/bookings/{bookingId}', {
      params: { path: { venueId, bookingId } },
    }),
  ) as Promise<BookingDetail>;

export const createBooking = (venueId: string, input: CreateBookingInput): Promise<BookingDetail> =>
  unwrap(
    apiClient.POST('/api/v1/venue-workspace/venues/{venueId}/bookings', {
      params: { path: { venueId } },
      body: input,
    }),
  ) as Promise<BookingDetail>;

export const editBooking = (
  venueId: string,
  bookingId: string,
  input: CreateBookingInput,
): Promise<BookingDetail> =>
  unwrap(
    apiClient.PUT('/api/v1/venue-workspace/venues/{venueId}/bookings/{bookingId}', {
      params: { path: { venueId, bookingId } },
      body: input,
    }),
  ) as Promise<BookingDetail>;

export const cancelBooking = (venueId: string, bookingId: string): Promise<BookingDetail> =>
  unwrap(
    apiClient.POST('/api/v1/venue-workspace/venues/{venueId}/bookings/{bookingId}/cancel', {
      params: { path: { venueId, bookingId } },
    }),
  ) as Promise<BookingDetail>;

export const payBooking = (
  venueId: string,
  bookingId: string,
  paymentMethod: PaymentMethod,
): Promise<BookingDetail> =>
  unwrap(
    apiClient.POST('/api/v1/venue-workspace/venues/{venueId}/bookings/{bookingId}/pay', {
      params: { path: { venueId, bookingId } },
      body: { paymentMethod },
    }),
  ) as Promise<BookingDetail>;

/**
 * Cancel one unredeemed unit on an unpaid Booking (#265): allowed up to the Venue's cancellation
 * cutoff before a slotted unit's slot start, any time for a slotless one. `booking.cancel` gates
 * this the same as whole-booking cancel.
 */
const cancelBookingUnit = (
  venueId: string,
  bookingId: string,
  unitId: string,
): Promise<BookingDetail> =>
  unwrap(
    apiClient.POST(
      '/api/v1/venue-workspace/venues/{venueId}/bookings/{bookingId}/units/{unitId}/cancel',
      {
        params: { path: { venueId, bookingId, unitId } },
      },
    ),
  ) as Promise<BookingDetail>;

/**
 * Mark one unredeemed unit on a PAID Booking refunded (#265) — state only, the money reversal
 * itself stays outside the system. Gated by `booking.refund`.
 */
const refundBookingUnit = (
  venueId: string,
  bookingId: string,
  unitId: string,
): Promise<BookingDetail> =>
  unwrap(
    apiClient.POST(
      '/api/v1/venue-workspace/venues/{venueId}/bookings/{bookingId}/units/{unitId}/refund',
      {
        params: { path: { venueId, bookingId, unitId } },
      },
    ),
  ) as Promise<BookingDetail>;

/** The live WaiverRecord behind a linked unit, with its status now. Gated by `venue.view` or `venue.manage`. */
const getWaiverRecord = (waiverRecordId: string): Promise<WaiverRecord> =>
  unwrap(
    administrationClient.GET('/api/v1/franchise-administration/waiver-records/{id}', {
      params: { path: { id: waiverRecordId } },
    }),
  ) as Promise<WaiverRecord>;

const revokeWaiverRecord = (waiverRecordId: string, reason: string): Promise<WaiverRecord> =>
  unwrap(
    administrationClient.POST(
      '/api/v1/franchise-administration/waiver-records/{id}/revoke' as never,
      {
        params: { path: { id: waiverRecordId } },
        body: { reason },
      } as never,
    ),
  ) as Promise<WaiverRecord>;

export function useBookings(
  venueId: string,
  filter: BookingDateFilter = { kind: 'today' },
  enabled = true,
) {
  return useQuery({
    queryKey: listKey(venueId, filter),
    queryFn: () => getBookings(venueId, filter),
    enabled: enabled && !!venueId,
  });
}

export function useBooking(venueId: string, bookingId: string | null, enabled = true) {
  return useQuery({
    queryKey: detailKey(venueId, bookingId ?? ''),
    queryFn: () => getBooking(venueId, bookingId as string),
    enabled: enabled && !!venueId && !!bookingId,
  });
}

export function useCreateBooking(venueId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateBookingInput) => createBooking(venueId, input),
    // Any list view (today / upcoming / a specific date) may now be stale.
    onSuccess: () => queryClient.invalidateQueries({ queryKey: bookingsKey(venueId) }),
  });
}

export function useEditBooking(venueId: string, bookingId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateBookingInput) => editBooking(venueId, bookingId, input),
    // The edited booking's detail and every list view that may show it are now stale.
    onSuccess: () => queryClient.invalidateQueries({ queryKey: bookingsKey(venueId) }),
  });
}

export function useCancelBooking(venueId: string, bookingId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => cancelBooking(venueId, bookingId),
    // The cancelled booking's detail and every list view that may show it are now stale.
    onSuccess: () => queryClient.invalidateQueries({ queryKey: bookingsKey(venueId) }),
  });
}

export function usePayBooking(venueId: string, bookingId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (paymentMethod: PaymentMethod) => payBooking(venueId, bookingId, paymentMethod),
    // The paid booking's detail and every list view that may show it are now stale.
    onSuccess: () => queryClient.invalidateQueries({ queryKey: bookingsKey(venueId) }),
  });
}

export function useCancelBookingUnit(venueId: string, bookingId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (unitId: string) => cancelBookingUnit(venueId, bookingId, unitId),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: bookingsKey(venueId) }),
  });
}

export function useRefundBookingUnit(venueId: string, bookingId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (unitId: string) => refundBookingUnit(venueId, bookingId, unitId),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: bookingsKey(venueId) }),
  });
}

/** The live WaiverRecord a linked unit points at, so the back office can show its current status. */
export function useWaiverRecord(waiverRecordId: string | null, enabled: boolean) {
  return useQuery({
    queryKey: waiverRecordKey(waiverRecordId ?? ''),
    queryFn: () => getWaiverRecord(waiverRecordId as string),
    enabled: enabled && waiverRecordId !== null,
  });
}

export function useRevokeWaiverRecord(venueId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ waiverRecordId, reason }: RevokeWaiverRecordInput) =>
      revokeWaiverRecord(waiverRecordId, reason),
    onSuccess: (record) => {
      queryClient.setQueryData(waiverRecordKey(record.id), record);
      return queryClient.invalidateQueries({ queryKey: bookingsKey(venueId) });
    },
  });
}
